/**
 * Los íconos de la navegación y de las entidades del sistema.
 *
 * Antes eran caracteres Unicode sueltos (`⚒`, `✉`, `⚠`, `★`…) puestos como
 * texto. El problema no era estético: iOS y Android sustituyen varios de
 * esos code points por su emoji a todo color, así que en el celular la
 * barra lateral aparecía con un martillo naranja y un sobre azul entre
 * glifos grises. Un SVG no se sustituye nunca, se ve igual en todos lados,
 * y hereda el color del texto (`currentColor`) — que es lo que hace que el
 * ítem activo, el inactivo y el hover se pinten solos.
 *
 * Trazo de 1.6 y grilla de 24 para todos: a 15px de ancho, que es como se
 * usan en la barra, un grosor parejo es lo que hace que el conjunto se lea
 * como una familia y no como íconos juntados de lugares distintos.
 */

import type { ReactNode } from 'react';

const BASE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Nombres de ícono válidos. Es un tipo, no un `string`: si alguien escribe
 *  mal el nombre en la navegación, no compila — antes un glifo equivocado
 *  solo se notaba mirando la pantalla. */
export type IconName =
  | 'panel'
  | 'clientes'
  | 'consultorio'
  | 'doctor'
  | 'calendario'
  | 'solicitudes'
  | 'comunicacion'
  | 'cobros'
  | 'finanzas'
  | 'obligaciones'
  | 'inventario'
  | 'proveedores'
  | 'personal'
  | 'calidad'
  | 'incidencias'
  | 'reportes'
  | 'catalogo'
  | 'configuracion'
  /* Densidad de la galería. */
  | 'densidad-detallada'
  | 'densidad-compacta'
  /* Las opciones de las tarjetas de `.radiocard`. */
  | 'reparto-regla'
  | 'reparto-bifurcado'
  | 'sin-cobro'
  | 'producto-seguro'
  | 'producto-peligroso'
  | 'confirmar'
  | 'anular'
  /* El menú de la persona que está usando el sistema. */
  | 'perfil'
  | 'salir'
  /* Las barras del portal del cliente y de la app del personal, y los estados
     vacíos. Nacieron como glifos Unicode (⌂ ◷ ☺ ☀ ★ ✉…) y en el teléfono
     varios se convertían en emoji a todo color — el mismo defecto que esta
     familia vino a eliminar del panel. */
  | 'inicio'
  | 'horas'
  | 'vacaciones'
  | 'servicios'
  | 'mejora'
  | 'mas'
  | 'lista'
  | 'documento'
  | 'intercambio'
  | 'volver'
  /* Los últimos glifos sueltos del sistema (☰ ✕ ◴ ⠿ ‹ › ★ i !), pasados a la
     familia en la auditoría de interfaces del 1-sep-2026. */
  | 'menu'
  | 'cerrar'
  | 'cargando'
  | 'arrastrar'
  | 'chevron-izq'
  | 'chevron-der'
  | 'estrella'
  | 'info'
  | 'alerta'
  | 'buscar'
  /* Los tres insumos que Clean Blue repone (CB-07), para que el cliente los
     elija por su forma y no solo por el nombre — la lista genérica de
     casillas no ayudaba a escoger rápido (dueño, 2-sep-2026). */
  | 'insumo-toallas'
  | 'insumo-papel'
  | 'insumo-jabon'
  /* Mostrar/ocultar la contraseña — reemplaza al botón de texto
     «Mostrar»/«Ocultar» de `PasswordInput` (dueño, 7-sep-2026). */
  | 'ojo'
  | 'ojo-cerrado'
  /* Los tres del panel (dueño, 8-set-2026: «que sea más sencillo visualmente
     entender por cobrar, cobrado, facturado»). Los otros tres recuadros no
     estrenan ícono: consultorios usa `consultorio`, las obligaciones de la
     semana usan `obligaciones` y las vencidas `alerta` — inventar un ícono
     nuevo para algo que ya tiene el suyo es lo que rompe una familia. */
  | 'por-cobrar'
  | 'cobrado'
  | 'facturado'
  /* Acciones compactas del estado mensual. */
  | 'pago-mas'
  | 'documento-mas'
  | 'pdf-mas'
  | 'xml-mas'
  | 'vincular'
  /* Quitar la foto. Es el único botón destructivo que quedó sin palabra, y por
     eso el ícono tiene que ser el que todo el mundo ya conoce. */
  | 'basurero';

const PATHS: Record<IconName, ReactNode> = {
  /* Panel: los módulos del tablero. */
  panel: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  /* Clientes: consultorio + su doctor, que es exactamente lo que agrupa. */
  clientes: (
    <>
      <path d="M3 10.5 11 4l8 6.5" />
      <path d="M5 10v10h12V10" />
      <circle cx="11" cy="13.6" r="1.9" />
      <path d="M7.8 19.4c0-1.9 1.4-3.2 3.2-3.2s3.2 1.3 3.2 3.2" />
    </>
  ),
  /* Consultorio: el espacio físico — puerta con su placa de número. */
  consultorio: (
    <>
      <path d="M4 20V5.5a1.5 1.5 0 0 1 1.5-1.5h10A1.5 1.5 0 0 1 17 5.5V20" />
      <path d="M2.5 20h18" />
      <path d="M13.6 12.2h.01" />
      <path d="M8 7.5h5" />
    </>
  ),
  /* Doctor: la persona, con el estetoscopio que la distingue del personal. */
  doctor: (
    <>
      <circle cx="12" cy="7.5" r="3.5" />
      <path d="M5.5 20.5c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
      <path d="M9.4 4.6V3M14.6 4.6V3" />
    </>
  ),
  calendario: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  /* Solicitudes: una bandeja con algo que entró y espera. */
  solicitudes: (
    <>
      <path d="M3 14h5l1.5 2.5h5L16 14h5" />
      <path d="M5.2 5.5 3 14v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4l-2.2-8.5A2 2 0 0 0 16.9 4H7.1a2 2 0 0 0-1.9 1.5Z" />
    </>
  ),
  comunicacion: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 6.5 8.5 6 8.5-6" />
    </>
  ),
  /* Cobros: el colón, que es la moneda con la que se cobra acá. */
  cobros: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.2 8.4a4.2 4.2 0 1 0 0 7.2" />
      <path d="M8.2 9.6h7.4M8.2 14.4h7.4" />
    </>
  ),
  finanzas: (
    <>
      <path d="M4 20V9M10 20V4M16 20v-8M22 20H2" />
    </>
  ),
  /* Obligaciones: lo que vence — un reloj, no un calendario, porque lo que
     importa es el plazo, no la fecha. */
  obligaciones: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6.8V12l3.4 2" />
    </>
  ),
  inventario: (
    <>
      <path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Z" />
      <path d="M3 7.5 12 12l9-4.5M12 12v9" />
    </>
  ),
  /* Proveedores: de quién viene lo que entra al inventario — el camión de
     reparto. No una caja (eso ya es `inventario`, y el par se leería como dos
     entradas de lo mismo) ni un edificio (eso es una sede, no un proveedor):
     lo que distingue a un proveedor es que TRAE. */
  proveedores: (
    <>
      <path d="M2.8 6.5h9.4v9.6H2.8z" />
      <path d="M12.2 9.8h3.5l3.3 3.1v3.2h-6.8z" />
      <circle cx="7.2" cy="18.2" r="2.1" />
      <circle cx="16.6" cy="18.2" r="2.1" />
      <path d="M9.3 18.2h5.2M2.8 16.1v2.1h2.3M18.7 18.2H21v-2.1" />
    </>
  ),
  /* Personal: el equipo — dos personas, no una herramienta (el `⚒` viejo se
     convertía en el emoji de los martillos cruzados). */
  personal: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5.6 6-5.6s6 2.3 6 5.6" />
      <path d="M16.2 5.2a3.2 3.2 0 0 1 0 5.9" />
      <path d="M17.4 14.8c2.1.6 3.6 2.5 3.6 5.2" />
    </>
  ),
  calidad: (
    <>
      <path d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9L12 3.5Z" />
    </>
  ),
  incidencias: (
    <>
      <path d="M10.3 4.3 2.6 17.5a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9.5v4M12 17.2h.01" />
    </>
  ),
  reportes: (
    <>
      <path d="M14 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8l-5-5Z" />
      <path d="M14 3v5h5" />
      <path d="M8.8 13.5h6.4M8.8 17h4.2" />
    </>
  ),
  catalogo: (
    <>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2 2 0 0 1 2 2v13a2 2 0 0 0-2-2H5.5A1.5 1.5 0 0 1 4 15.5v-10Z" />
      <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H14a2 2 0 0 0-2 2v13a2 2 0 0 1 2-2h4.5a1.5 1.5 0 0 0 1.5-1.5v-10Z" />
    </>
  ),
  configuracion: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 14.6a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.2a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
    </>
  ),

  /* ── Densidad de la galería ───────────────────────────────────────────────
     Más cuadrados = más tarjetas por pantalla = compacto. Es la convención de
     Finder, Drive y Figma; invertirla haría que el usuario elija al revés.

     `densidad-detallada` se parece a `panel` porque el dibujo obvio de «pocos
     cuadrados» es uno solo. Son dos entradas igual: `panel` nombra el tablero
     en la navegación, y si algún día se redibuja, el conmutador de la galería
     no tiene por qué cambiar con él. Comparten borde (3 a 21) con su pareja,
     que es lo que hace que se lean como un par y no como dos íconos sueltos. */
  'densidad-detallada': (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.6" />
      <rect x="13" y="3" width="8" height="8" rx="1.6" />
      <rect x="3" y="13" width="8" height="8" rx="1.6" />
      <rect x="13" y="13" width="8" height="8" rx="1.6" />
    </>
  ),
  'densidad-compacta': (
    <>
      <rect x="3" y="3" width="4.6" height="4.6" rx="1" />
      <rect x="9.7" y="3" width="4.6" height="4.6" rx="1" />
      <rect x="16.4" y="3" width="4.6" height="4.6" rx="1" />
      <rect x="3" y="9.7" width="4.6" height="4.6" rx="1" />
      <rect x="9.7" y="9.7" width="4.6" height="4.6" rx="1" />
      <rect x="16.4" y="9.7" width="4.6" height="4.6" rx="1" />
      <rect x="3" y="16.4" width="4.6" height="4.6" rx="1" />
      <rect x="9.7" y="16.4" width="4.6" height="4.6" rx="1" />
      <rect x="16.4" y="16.4" width="4.6" height="4.6" rx="1" />
    </>
  ),

  /* ── Las tres opciones del reparto de un bloque ───────────────────────────
     «Como siempre»: la flecha que vuelve sobre sí misma — lo que ya venía
     pasando sigue pasando. No es un reloj ni un calendario: lo que se elige no
     es una fecha, es seguir la regla vigente. */
  'reparto-regla': (
    <>
      <path d="M20 12a8 8 0 1 1-8-8c2.24 0 4.38.89 5.96 2.46L20 7" />
      {/* La punta cae fuera del anillo a propósito: probada adentro, se
          empastaba con el trazo del arco y a 16px el ícono se leía como un
          spinner de carga en vez de una flecha. */}
      <path d="M20 2.8V7h-4.2" />
    </>
  ),
  /* «Estos meses los pagan otros»: uno se abre en dos. Los nodos son círculos
     —partes, personas— y no puntas de flecha: el reparto no tiene dirección,
     tiene destinatarios. */
  'reparto-bifurcado': (
    <>
      <circle cx="6.1" cy="12" r="2.4" />
      <path d="M8.5 12c3.2 0 2.4-5 5.6-5" />
      <path d="M8.5 12c3.2 0 2.4 5 5.6 5" />
      <circle cx="16.7" cy="7" r="2.6" />
      <circle cx="16.7" cy="17" r="2.6" />
    </>
  ),
  /* «No cobrar estos meses»: la opción destructiva. Es el signo de prohibición
     —círculo con barra— y no un triángulo de alerta ni una equis roja: tiene
     que distinguirse de las otras dos sin que parezca que algo salió mal. La
     barra va de borde a borde del círculo, que es lo que lo hace leer como
     «no» y no como una moneda rayada. */
  'sin-cobro': (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M5.99 18.01 18.01 5.99" />
    </>
  ),

  /* ── Peligrosidad de un producto de inventario ────────────────────────────
     Los dos comparten el escudo y cambian solo lo de adentro: la decisión es
     una sola —si necesita hoja de seguridad o no— y el par tiene que leerse
     como las dos caras de esa misma pregunta. El escudo, y no el triángulo de
     `incidencias`, porque acá se está clasificando un producto, no avisando de
     un problema. */
  'producto-seguro': (
    <>
      <path d="M12 3.2 4.8 6.1v5.4c0 4.4 3 7.5 7.2 9.3 4.2-1.8 7.2-4.9 7.2-9.3V6.1L12 3.2Z" />
      <path d="m8.9 12.1 2.2 2.2 4.1-4.4" />
    </>
  ),
  'producto-peligroso': (
    <>
      <path d="M12 3.2 4.8 6.1v5.4c0 4.4 3 7.5 7.2 9.3 4.2-1.8 7.2-4.9 7.2-9.3V6.1L12 3.2Z" />
      <path d="M12 8.2v4.4M12 16h.01" />
    </>
  ),

  /* ── El veredicto de conciliación ─────────────────────────────────────────
     Mismo círculo, distinto adentro, por la misma razón que el par anterior:
     son las dos salidas de una sola pregunta. La equis va *dentro* del
     círculo — la barra de borde a borde ya es `sin-cobro`, y confundir «este
     cobro no corresponde» con «no cobrar el mes» sería caro. */
  confirmar: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.2 12.3 2.7 2.7 5-5.4" />
    </>
  ),
  anular: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m9.2 9.2 5.6 5.6M14.8 9.2l-5.6 5.6" />
    </>
  ),

  /* ── El menú de la persona que usa el sistema ─────────────────────────────
     `perfil` es la persona *dentro de un círculo*: eso es lo que lo separa de
     `doctor` (persona con estetoscopio) y de `personal` (dos personas). El
     disco es la convención de «esta cuenta, la tuya», la misma forma que ya
     tiene el avatar del menú. */
  perfil: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="9.8" r="3" />
      <path d="M6.1 18.8a5.9 5.9 0 0 1 11.8 0" />
    </>
  ),
  /* `salir`: la puerta abierta del lado por el que se sale, y la flecha
     saliendo por ahí. El panel se dibuja abierto a la derecha a propósito —
     una caja cerrada con una flecha encima se lee como «exportar». */
  salir: (
    <>
      <path d="M13.5 4.5H6.5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h7" />
      <path d="M15.5 8.4 19.6 12l-4.1 3.6" />
      <path d="M9.6 12h9.8" />
    </>
  ),
  /* Inicio: la casa, simple — es el «⌂» que había, dibujado. */
  inicio: (
    <>
      <path d="M4 10.5 12 4l8 6.5" />
      <path d="M6 10v10h12V10" />
    </>
  ),
  /* Horas: un reloj marcando — reemplaza al «◷». */
  horas: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2.5" />
    </>
  ),
  /* Vacaciones: el sol — el «☀» sin riesgo de volverse emoji. */
  vacaciones: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 3v2.4M12 18.6V21M3 12h2.4M18.6 12H21M5.6 5.6l1.7 1.7M16.7 16.7l1.7 1.7M18.4 5.6l-1.7 1.7M7.3 16.7l-1.7 1.7" />
    </>
  ),
  /* Servicios: el destello de limpio, cuatro puntas — era el «✦». */
  servicios: (
    <path d="M12 4c.7 3.6 2.4 5.3 6 6-3.6.7-5.3 2.4-6 6-.7-3.6-2.4-5.3-6-6 3.6-.7 5.3-2.4 6-6Z" />
  ),
  /* Mejora: la estrella — el «★» que en iOS salía dorado. */
  mejora: (
    <path d="m12 4 2.35 4.9 5.4.7-3.95 3.75.95 5.35L12 16.15 7.25 18.7l.95-5.35L4.25 9.6l5.4-.7Z" />
  ),
  /* Más: los tres puntos de «hay más acá». */
  mas: (
    <>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  /* Lista: tres renglones — el «≡» de los listados vacíos. */
  lista: (
    <path d="M4.5 7h15M4.5 12h15M4.5 17h15" />
  ),
  /* Documento: la hoja con la esquina doblada — facturas y comprobantes. */
  documento: (
    <>
      <path d="M6.5 3.5h7L18 8v12.5H6.5Z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M9 12.5h6M9 16h6" />
    </>
  ),
  /* Intercambio: ida y vuelta — conciliar, mover, cruzar. */
  intercambio: (
    <path d="M4.5 8.5h13l-3-3M19.5 15.5h-13l3 3" />
  ),
  /* Volver: la flecha. Reemplaza a los botones que decían «Volver» en texto:
     menos ruido visual, mismo destino (pedido del dueño, 27-ago-2026). */
  volver: (
    <path d="M19 12H5.5M11 5.5 4.5 12l6.5 6.5" />
  ),
  /* Menú: la hamburguesa del drawer del panel en el teléfono. */
  menu: (
    <path d="M4.5 7h15M4.5 12h15M4.5 17h15" />
  ),
  /* Cerrar: la cruz de los modales. */
  cerrar: (
    <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
  ),
  /* Cargando: el arco abierto — se anima con CSS (`.ico-girando`). */
  cargando: (
    <path d="M12 3.5a8.5 8.5 0 1 1-8.5 8.5" />
  ),
  /* Arrastrar: el asa de seis puntos para reordenar. */
  arrastrar: (
    <>
      <circle cx="9" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  /* Chevrones: mes anterior / siguiente del calendario, paginación. */
  'chevron-izq': (
    <path d="M14.5 5.5 8 12l6.5 6.5" />
  ),
  'chevron-der': (
    <path d="M9.5 5.5 16 12l-6.5 6.5" />
  ),
  /* Estrella: la calificación. Se rellena con `fill="currentColor"` vía CSS
     cuando está elegida (`.rate-stars__star.is-on svg { fill: currentColor }`). */
  estrella: (
    <path d="M12 3.6l2.5 5.3 5.8.7-4.3 4 1.1 5.8L12 16.6l-5.1 2.8 1.1-5.8-4.3-4 5.8-.7Z" />
  ),
  /* Info y alerta: los tonos de `Note`. */
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5M12 7.8v.4" />
    </>
  ),
  alerta: (
    <>
      <path d="M12 4 3.5 19h17Z" />
      <path d="M12 9.5v4.5M12 16.5v.4" />
    </>
  ),
  /* Buscar: la lupa de los buscadores. */
  buscar: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M15 15l5 5" />
    </>
  ),
  /* Toallas de papel: el rollo de pie, visto de lado (cilindro con el tubo
     de cartón asomando arriba). */
  'insumo-toallas': (
    <>
      <ellipse cx="12" cy="5.3" rx="5.3" ry="1.9" />
      <path d="M6.7 5.3v13c0 1.05 2.37 1.9 5.3 1.9s5.3-.85 5.3-1.9v-13" />
      <ellipse cx="12" cy="5.3" rx="1.7" ry="0.6" />
    </>
  ),
  /* Papel higiénico: el rollo de frente (dos círculos) con la tira colgando
     y el borde perforado — así no se confunde con el de toallas. */
  'insumo-papel': (
    <>
      <circle cx="9" cy="8.7" r="5.3" />
      <circle cx="9" cy="8.7" r="1.9" />
      <path d="M13.3 12.3h5v6" />
      <path d="M15.8 18.3l1 1.3 1-1.3 1 1.3" />
    </>
  ),
  /* Jabón de manos: el dispensador de bomba, con la gota que cae. */
  'insumo-jabon': (
    <>
      <rect x="7.3" y="9.8" width="9" height="10.9" rx="2" />
      <path d="M10.8 9.8V7.3a1.4 1.4 0 0 1 1.4-1.4" />
      <path d="M12.2 5.9h4.3" />
      <path d="M17.3 8.3c0 .95-.7 1.5-1.4 1.5s-1.4-.55-1.4-1.5c0-.75 1.4-2.5 1.4-2.5s1.4 1.75 1.4 2.5Z" />
    </>
  ),
  /* Ojo abierto: «mostrar la contraseña». El párpado es un arco simple, no un
     óvalo cerrado, para que se distinga de `info`/`obligaciones` a 16px. */
  ojo: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  /* Ojo cerrado: mismo párpado, con la pupila tachada por una sola diagonal —
     «ocultar», y no un segundo ícono sin relación con el primero. */
  'ojo-cerrado': (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.8" />
      <path d="M4.5 19.5 19.5 4.5" />
    </>
  ),

  /* ── Los recuadros del panel ────────────────────────────────────────────
     «Por cobrar» y «Cobrado» comparten la MISMA bandeja y se diferencian solo
     por lo que pasa arriba: una flecha que baja contra un visto. Es a
     propósito — son el mismo dinero en dos momentos, y dos íconos sin parecido
     obligarían a leer el rótulo para saber cuál es cuál, que es exactamente lo
     que estos íconos vienen a evitar. */
  'por-cobrar': (
    <>
      <path d="M12 3.5v8.5" />
      <path d="M8.6 8.9 12 12.3l3.4-3.4" />
      <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
    </>
  ),
  cobrado: (
    <>
      <path d="M7.1 9.5 10.7 13.1 17 6.8" />
      <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
    </>
  ),
  /* La hoja de `documento`, pero con el colón adentro en vez de los dos
     renglones: lo que distingue una factura de un papel cualquiera es que
     lleva plata.
     El ₡ se dibuja con el MISMO gesto que `cobros` —el arco abierto a la
     derecha y los dos trazos que lo cruzan—, no con una barra vertical: la
     barra vertical daba un «¢», que es el signo de centavo y no el del colón,
     y encima a este tamaño se leía como un garabato dentro de la hoja
     (verificado agrandando el panel en pantalla, no mirando el path). */
  facturado: (
    <>
      <path d="M6.5 3.5h7L18 8v12.5H6.5Z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M14.4 11.9a2.9 2.9 0 1 0 0 5.2" />
      <path d="M9.6 12.9h5.4M9.6 16.1h5.4" />
    </>
  ),
  'pago-mas': (
    <>
      <circle cx="10.5" cy="12" r="7.5" />
      <path d="M13.2 8.6a3.8 3.8 0 1 0 0 6.8M7.2 10h6.5M7.2 14h6.5" />
      <path d="M18.5 3.5v5M16 6h5" />
    </>
  ),
  'documento-mas': (
    <>
      <path d="M5.5 3.5h7L17 8v12.5H5.5Z" />
      <path d="M12.5 3.5V8H17M9 13h5M11.5 10.5v5" />
      <path d="M18.5 12.5v6M15.5 15.5h6" />
    </>
  ),
  'pdf-mas': (
    <>
      <path d="M5.5 3.5h7L17 8v12.5H5.5Z" />
      <path d="M12.5 3.5V8H17M8 12.5h6M8 15.5h4" />
      <path d="M18.5 12.5v6M15.5 15.5h6" />
    </>
  ),
  'xml-mas': (
    <>
      <path d="M5.5 3.5h7L17 8v12.5H5.5Z" />
      <path d="M12.5 3.5V8H17M10 12l-2 2 2 2M13 12l2 2-2 2" />
      <path d="M19 12.5v6M16 15.5h6" />
    </>
  ),
  vincular: (
    <>
      <path d="m9.7 14.3 4.6-4.6" />
      <path d="M7.9 16.1 6.5 17.5a3.2 3.2 0 0 1-4.5-4.5l3.1-3.1a3.2 3.2 0 0 1 4.5 0" />
      <path d="m16.1 7.9 1.4-1.4A3.2 3.2 0 1 1 22 11l-3.1 3.1a3.2 3.2 0 0 1-4.5 0" />
    </>
  ),
  basurero: (
    <>
      <path d="M4 6.8h16" />
      <path d="M9.6 6.8V5.4A1.4 1.4 0 0 1 11 4h2a1.4 1.4 0 0 1 1.4 1.4v1.4" />
      <path d="M6.6 6.8 7.4 19a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4l.8-12.2" />
      <path d="M10.4 10.6v6M13.6 10.6v6" />
    </>
  ),
};

/** Un ícono del sistema. `aria-hidden` por defecto: en la navegación el
 *  nombre ya va escrito al lado, así que anunciarlo de nuevo solo estorba a
 *  quien usa lector de pantalla. Cuando el ícono va solo (sin texto que lo
 *  acompañe), pasar `title` para que sí se anuncie. */
/** Si un texto nombra un ícono de esta familia. Lo usa `Empty` para aceptar
 *  nombres sin que cada pantalla tenga que importar nada. */
export function isIconName(v: string): v is IconName {
  return v in PATHS;
}

export function Icon({ name, size = 16, title }: { name: IconName; size?: number; title?: string }) {
  return (
    <svg
      {...BASE}
      width={size}
      height={size}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {PATHS[name]}
    </svg>
  );
}

/**
 * NFC: el chip físico — una moneda — con sus ondas de contacto.
 *
 * Antes esto era un pentágono con punta y un agujerito: una etiqueta de precio
 * colgante, de las que se atan a la ropa. El objeto real no es una etiqueta,
 * es un disco que se pega al consultorio, así que el dibujo mentía sobre lo
 * que el admin va a tener en la mano. Ahora es el disco, con el reborde que
 * tienen las monedas de verdad, y las ondas adentro — que son lo que se
 * reconoce como NFC aunque el resto del dibujo cambie.
 *
 * No es de la misma familia que `Icon` — esa es de trazo único a 24, pensada
 * para ir junto a texto en la navegación. Esta es a dos tonos (`--navy` /
 * `--brand`) sobre grilla de 48, como los íconos de servicios de la landing
 * (`ICONO_*` en `Landing.tsx`): un glifo Unicode no se distingue de puntuación
 * suelta, y esto va como marca visual junto a un título de card, no en una
 * lista apretada de ítems.
 */
export function IconNfc({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="24" cy="24" r="20" fill="var(--brand)" stroke="var(--navy)" strokeWidth="2" />
      {/* El reborde: es lo que separa un disco de un botón. */}
      <circle cx="24" cy="24" r="16.5" fill="none" stroke="var(--navy)" strokeWidth="1.2" />
      {/* Tres arcos crecientes y ningún punto de origen: es la marca de
          contactless que ya está en las tarjetas y en los datáfonos. Con un
          punto adelante —se probó— el dibujo se lee como un parlante. */}
      <path d="M17.5 18.64A7 7 0 0 1 17.5 29.36" fill="none" stroke="var(--navy)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M20.71 14.81A12 12 0 0 1 20.71 33.19" fill="none" stroke="var(--navy)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M23.93 10.98A17 17 0 0 1 23.93 37.02" fill="none" stroke="var(--navy)" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
